import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { Helmet } from "react-helmet-async";
import CustomInput from "../../../common/CustomInput";
import CustomPasswordInput from "../../../common/CustomPasswordInput";
import { Images } from "../../../../config/images";
import "./TeacherLogin.css";
import { useTeanentLogin } from "../../../../hooks/tenant/useTenantLogin";
import { Button } from "react-bootstrap";

const TeacherLogin = () => {
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const { loading, error, tenantLogin } = useTeanentLogin();

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const initialValues = {
    email: "",
    password: "",
    rememberMe: false,
  };

  const validationSchema = Yup.object().shape({
    email: Yup.string()
      .required("This field is required")
      .test("email-or-username", "Enter valid email or username", (value) => {
        if (!value) return false;

        if (value.includes("@")) {
          // Validate a s email
          return Yup.string().email().isValidSync(value);
        }

        // Validate as username
        return /^[a-zA-Z0-9._-]+$/.test(value);
      }),
    password: Yup.string().required("Password is required"),
  });

  const handleLogin = async (values) => {
    const { email, password } = values;

    const payload = email.includes("@")
      ? { email, password }
      : { username: email, password };

    await tenantLogin(payload);
  };

  return (
    <>
      <Helmet>
        <title>Login | School Login</title>
      </Helmet>

      <section className="tenant-login-wrapper">
        <div className="tenant-login-card">
          {/* Logo */}
          <div className="tenant-login-logo">
            <img src={Images.Logo} alt="logo" onClick={() => navigate("/")} />
            <p className="tenant-login-title">Welcome Back!</p>
          </div>

          {/* Form */}
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleLogin}
          >
            {({ isSubmitting }) => (
              <Form className="d-flex flex-column gap-3">
                <CustomInput
                  label="Username / Email"
                  name="email"
                  type="text"
                  placeholder="Enter your email"
                  className="custom-primary-input"
                  layout="stacked"
                  ref={inputRef}
                />
                <CustomPasswordInput
                  label="Password"
                  name="password"
                  type="password"
                  placeholder="Enter your password"
                  className="custom-primary-input"
                  layout="stacked"
                />

                <div>
                  {/* Remember Me + Forgot Password */}
                  <div className="tenant-form-row">
                    <label className="tenant-remember">
                      <Field
                        type="checkbox"
                        name="rememberMe"
                        className="login-check-input"
                      />
                      <div>Remember me</div>
                    </label>

                    <span
                      className="tenant-forgot-password"
                      onClick={() =>
                        (window.location.href = "/forgot-password")
                      }
                    >
                      Forgot Password
                    </span>
                  </div>

                  {/* API Error */}
                  <div>
                    {error && (
                      <div className="custom-error text-center">{error}</div>
                    )}
                  </div>

                  {/* Submit Button */}
                  <Button
                    type="submit"
                    className="custom-primary-button"
                    disabled={loading || isSubmitting}
                  >
                    {loading ? "Signing in..." : "Sign in"}
                  </Button>

                  {/* Back to Home Button */}
                  <div className="go-back-container">
                    <div
                      className="go-back-btn"
                      onClick={() => navigate("/")}
                    >
                      <i className="bi bi-arrow-left icon-shadow"></i>
                      <div>Back to Home</div>
                    </div>
                  </div>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </section>
    </>
  );
};

export default TeacherLogin;
